import { useLocalSearchParams } from 'expo-router';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useQuery } from 'urql';
import * as Placeholder from '@/components/Custom/Placeholder';
import EventList from '@/components/Event/EventList';
import {
  EVENT_LIMIT,
  getRelativeTimeInfo,
  isNextUpcomingEventListItem,
} from '@/components/Event/EventList.utils';
import * as EventListItem from '@/components/Event/EventListItem';
import { EventListItemInfoFragment } from '@/components/Event/EventListItemInfo';
import { EventListItemTeamsFragment } from '@/components/Event/EventListItemTeams';
import { EventListItemTimeFragment } from '@/components/Event/EventListItemTime';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import WidgetPoweredBy from '@/components/Widget/WidgetPoweredBy';
import { graphql } from '@/gql/graphql';
import useDateCursorBidirectionalList from '@/hooks/useDateCursorBidirectionalList';

const GetWidgetSchedule = graphql(
  /* GraphQL */ `
    query GetWidgetSchedule($eventId: uuid!) {
      event_by_pk(id: $eventId) {
        id
        title
      }
    }
  `,
);

const GetWidgetScheduleListFuture = graphql(/* GraphQL */ `
  query GetWidgetScheduleListFuture($where: event_bool_exp!, $limit: Int!) {
    event(
      order_by: [
        { datetime: asc }
        { title: asc }
        { location: asc }
        { id: desc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemInfo
      ...EventListItemTeams
      ...EventListItemTime
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTimeFragment,
]);

const GetWidgetScheduleListPast = graphql(/* GraphQL */ `
  query GetWidgetScheduleListPast($where: event_bool_exp!, $limit: Int!) {
    event(
      order_by: [
        { datetime: desc }
        { title: desc }
        { location: desc }
        { id: asc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemInfo
      ...EventListItemTeams
      ...EventListItemTime
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTimeFragment,
]);

export default function WidgetSchedule() {
  const [anchorDate] = useState(() => DateTime.local().toISO());
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();

  const [{ data: eventData }] = useQuery({
    query: GetWidgetSchedule,
    variables: {
      eventId: eventId!,
    },
  });

  const event = eventData?.event_by_pk;

  const bidirectionalList = useDateCursorBidirectionalList({
    anchorValue: anchorDate,
    baseWhere: { parent_id: { _eq: eventId }, event_type: { _eq: 'game' } },
    directionLimit: EVENT_LIMIT,
    fieldName: 'event',
    futureQuery: GetWidgetScheduleListFuture,
    pastQuery: GetWidgetScheduleListPast,
    selectFutureItems: (data) => data?.event ?? [],
    selectPastItems: (data) => data?.event ?? [],
  });

  return (
    <Box direction="vertical" flex>
      <Box py="xl" borderBottomWidth={2} border="divider">
        <Text bold numberOfLines={1} size="2xl" center>
          {eventData?.event_by_pk?.title}
        </Text>
      </Box>
      <EventList
        data={bidirectionalList.listData}
        fetching={
          bidirectionalList.isInitialFetching ||
          bidirectionalList.isListFetching
        }
        divideUpcoming
        maintainVisibleContentPosition
        hideScrollButton
        onEndReached={bidirectionalList.fetchNextPage}
        onStartReached={bidirectionalList.fetchPreviousPage}
        renderItem={({ item, data, index, extraData }) => {
          const nowMs =
            typeof extraData === 'number' ? extraData * 60000 : undefined;
          const relativeTimeInfo = getRelativeTimeInfo({
            status: item?.status,
            hasClock: !!item?.event_clock?.event_id,
            datetime: item?.datetime,
            nowMs,
            isNextUpcomingEvent: isNextUpcomingEventListItem({
              data,
              index,
              nowMs,
            }),
          });

          return (
            <EventListItem.Container relativeTimeInfo={relativeTimeInfo}>
              <EventListItem.Header>
                <EventListItem.Time
                  fragment={item}
                  relativeTimeInfo={relativeTimeInfo}
                />
                <EventListItem.Info
                  fragment={item}
                  relativeTimeInfo={relativeTimeInfo}
                />
              </EventListItem.Header>
              <EventListItem.Content>
                <EventListItem.Teams fragment={item} />
              </EventListItem.Content>
            </EventListItem.Container>
          );
        }}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Icon name="calendar-outline" />
            <Placeholder.Text>
              {event?.title} has no events scheduled yet.
            </Placeholder.Text>
          </Placeholder.Container>
        }
      />
      <WidgetPoweredBy />
    </Box>
  );
}
